import { useState } from 'react';

export default function Home() {
  const stats = [
    { 
      label: '$3B+ in capital originated and structured across venture and infrastructure projects',
      icon: 'ri-money-dollar-circle-line' 
    },
    { 
      label: '$4M+ raised and managed profitably across 20+ years of entrepreneurial operations',
      icon: 'ri-line-chart-line' 
    },
    { 
      label: '275+ projects financed or advised globally; 300+ startups and SMEs supported',
      icon: 'ri-folder-line' 
    },
    { 
      label: '~1.0 MtCO₂e/year in emissions reductions from renewable energy and industrial decarbonization initiatives',
      icon: 'ri-leaf-line' 
    },
    { 
      label: '3→50 employees scaled at tech venture; 3 cross-border offices (Los Angeles, Silicon Valley, Seoul) established for infrastructure finance operations',
      icon: 'ri-building-line' 
    },
    { 
      label: '16 professionals trained and mentored; 6 advanced to leadership or founded new ventures',
      icon: 'ri-team-line' 
    }
  ];

  const experiences = [
    {
      company: 'Vnesto Capital',
      role: 'Founder & Managing Director',
      period: '2008–Present',
      description: 'Founded and led a climate-finance and development advisory platform specializing in blended capital solutions for industrial decarbonization, clean energy, and waste-to-value infrastructure. • Raised $4M+ in seed capital; managed financial operations, budgeting, and runway planning while maintaining profitability throughout 15+ years of operations • Conducted financial modeling, sensitivity analysis, and capital structuring for 275+ projects totaling $3B+ across climate tech, industrial decarbonization, and sustainable infrastructure • Established operational hubs in Los Angeles, Silicon Valley, and Seoul; directed cross-functional teams executing policy-aligned programs and market-entry strategies across Asia • Designed and executed blended capital solutions advancing measurable ESG outcomes; served as OPIC/DFC Loan Originator for catalytic infrastructure investments in emerging markets • Built AI-driven business development platform improving investor outreach efficiency 4× and reducing research time 70%.'
    },
    {
      company: 'Charo CDC / U.S. SBA',
      role: 'Business Development Officer',
      period: '2006–2008',
      description: 'Delivered growth capital and strategic guidance to small businesses; increased loan production 47% through refined financial assessment, underwriting analysis, and pipeline management. Structured development financing aligned with SBA requirements, improving capital access in underserved markets.'
    },
    {
      company: 'Libris',
      role: 'Co-Founder',
      period: '2003–2006',
      description: 'Co-founded early-stage technology venture pioneering digital publishing infrastructure and print-on-demand solutions (pre-Kindle era). • Raised $1.35M in seed capital and managed financial operations through rapid scaling phase • Scaled operations from 3 to 50 employees while coordinating cross-functional teams across engineering, product development, and business operations • Led business development and publisher partnerships; secured licensing rights to 100,000+ book titles across academic and specialty publishing segments • Worked directly with lead programmer and mechanical engineer to design and build proprietary print-on-demand hardware and software platform • Navigated technical and market adoption challenges in emerging digital publishing landscape; gained hands-on experience in venture capital dynamics, product-market fit, and strategic decision-making under investor pressure'
    }
  ];

  const accomplishments = [
    {
      title: 'Financial Analysis & Modeling',
      description: 'Led sensitivity analysis, financial projections, and valuation modeling for $3B+ in capital transactions across multiple currencies and regulatory environments; structured debt packages including DSCR analysis, security arrangements, and subordinated capital waterfalls.'
    },
    {
      title: 'Technology Venture Operations',
      description: 'Co-founded and scaled tech startup from concept through growth phase; managed P&L, runway planning, and cross-functional teams (engineering, product, business development) through rapid scaling (3→50 employees in 3 years).'
    },
    {
      title: 'Capital Formation & Investor Relations',
      description: 'Raised $5.35M+ across venture and infrastructure platforms; cultivated relationships with DFIs, family offices, and strategic investors through data-driven due diligence, investor communications, and portfolio management.'
    },
    {
      title: 'Innovative Technology Solutions',
      description: 'Co-created AI-driven outreach platform automating investor research and personalized engagement, improving business development conversion rates 4× and reducing manual research time 70%.'
    },
    {
      title: 'Cross-Functional Leadership',
      description: 'Built and led teams across Los Angeles, Silicon Valley, and Seoul to execute finance, operations, and policy programs; coordinated stakeholders across technical, legal, finance, and government functions.'
    },
    {
      title: 'Strategic Advisory & Capital Deployment',
      description: 'Advised 300+ startups and SMEs across climate tech, energy, and infrastructure ecosystems; served as OPIC/DFC Loan Originator channeling catalytic capital to high-impact projects in emerging markets.'
    },
    {
      title: 'Investor Due Diligence & Communications',
      description: 'Managed data-room operations and cross-functional responses across finance, legal, and compliance; produced investor memos, market updates, and strategy briefings for GPs and LPs.'
    },
    {
      title: 'Entrepreneurial Leadership',
      description: 'Built internal systems, partnerships, and operational infrastructure for multiple ventures from inception, spanning finance, business development, team development, and cross-border market expansion.'
    }
  ];

  const technicalCapabilities = [
    {
      title: 'Financial Analysis & Planning',
      skills: 'Sensitivity analysis, scenario modeling, financial projections, valuation modeling, DSCR analysis, capital structure design, P&L management, runway planning'
    },
    {
      title: 'Strategic Operations',
      skills: 'Cross-functional team leadership, stakeholder coordination, process optimization, organizational scaling, data-driven decision-making'
    },
    {
      title: 'Technology & Data',
      skills: 'AI/automation platforms, CRM systems, business intelligence tools, data analytics, technical product development'
    },
    {
      title: 'Capital Markets',
      skills: 'Venture fundraising, investor relations, due diligence management, blended finance structures, debt structuring'
    },
    {
      title: 'International Finance',
      skills: 'Multi-currency transactions, cross-border regulatory compliance, political risk assessment, emerging markets'
    }
  ];

  const honors = [
    'Loan Originator — OPIC/DFC Enterprise Development Network',
    'Ranked #25 Business Leader of the Future — Latino Leaders Magazine',
    'Business Leader of the Year Nominee — Telemundo Latino Business Awards'
  ];

  const volunteerActivities = [
    {
      organization: 'Tzu Chi Buddhist Charitable Foundation',
      role: 'Volunteer, Health Equity Initiative',
      period: '2023–Present'
    },
    {
      organization: 'Executive Service Corps of Southern California',
      role: 'Leadership Coach',
      period: '2018–Present'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header Section */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-bold text-emerald-700 flex items-center gap-2">
            <i className="ri-leaf-fill w-6 h-6 flex items-center justify-center"></i>
            RT
          </div>
          <nav className="flex gap-6">
            <a href="#summary" className="text-slate-700 hover:text-emerald-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Summary</a>
            <a href="#impact" className="text-slate-700 hover:text-emerald-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Impact</a>
            <a href="#accomplishments" className="text-slate-700 hover:text-emerald-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Accomplishments</a>
            <a href="#experience" className="text-slate-700 hover:text-emerald-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Experience</a>
            <a href="#education" className="text-slate-700 hover:text-emerald-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Education</a>
          </nav>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Hero Section */}
        <section className="text-center mb-20">
          <div className="mb-6">
            <div className="w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden shadow-xl border-4 border-emerald-200/50 relative">
              <img 
                src="https://static.readdy.ai/image/68e2e2bb5944c6b03e4952c4bfda37bd/ed117726003f28380ead5dfb6fbc2d28.jpeg" 
                alt="Ricardo Tejada - Climate Tech Executive"
                className="w-full h-full object-cover object-top scale-150"
                style={{ objectPosition: 'center 20%' }}
              />
            </div>
          </div>
          <h1 className="text-5xl font-bold text-slate-900 mb-4">Ricardo Tejada</h1>
          <p className="text-lg text-slate-600 mb-8 max-w-3xl mx-auto">
            Managing Director | Strategic Finance & Capital Formation | Technology & Infrastructure Ventures
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="mailto:retejada@gmail.com" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg shadow-md transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
              Email
            </a>
            <a 
              href="https://www.linkedin.com/in/ricardoetejada" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-slate-50 text-emerald-700 rounded-lg border-2 border-emerald-600 shadow-md transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-linkedin-fill w-5 h-5 flex items-center justify-center"></i>
              LinkedIn
            </a>
          </div>
        </section>

        {/* Executive Summary */}
        <section id="summary" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-file-text-line w-8 h-8 flex items-center justify-center"></i>
            Executive Summary
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-md">
            <p className="text-slate-700 leading-relaxed text-lg">
              Entrepreneurial founder and strategic finance leader with 20+ years building and scaling technology and infrastructure ventures. Co-founded tech startup (3→50 employees, $1.35M raised); founded and led climate finance platform mobilizing <strong className="text-emerald-700">$3B+</strong> across 275+ projects globally. Combines hands-on operational experience with complex financial modeling, cross-functional team leadership, and data-driven decision-making to execute high-impact strategic initiatives. Proven ability to navigate technical complexity, stakeholder dynamics, and market uncertainty while maintaining financial discipline.
            </p>
          </div>
        </section>

        {/* Impact Snapshot */}
        <section id="impact" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-bar-chart-box-line w-8 h-8 flex items-center justify-center"></i>
            Impact Metrics & Portfolio Snapshot
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-emerald-500 hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <i className={`${stat.icon} text-2xl text-emerald-600 w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="text-slate-800 font-semibold">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Accomplishments */}
        <section id="accomplishments" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-trophy-line w-8 h-8 flex items-center justify-center"></i>
            Key Accomplishments
          </h2>
          <div className="space-y-4">
            {accomplishments.map((item, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-6 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                <h3 className="text-xl font-bold text-emerald-700 mb-2">{item.title}</h3>
                <p className="text-slate-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Experience */}
        <section id="experience" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-briefcase-line w-8 h-8 flex items-center justify-center"></i>
            Experience
          </h2>
          <div className="space-y-6">
            {experiences.map((exp, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-xl p-8 hover:border-emerald-500 hover:shadow-lg transition-all"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">{exp.company}</h3>
                    <p className="text-lg text-emerald-700 font-semibold">{exp.role}</p>
                  </div>
                  <div className="text-slate-600 mt-2 md:mt-0 whitespace-nowrap">{exp.period}</div>
                </div>
                <p className="text-slate-700 leading-relaxed">{exp.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Technical Capabilities */}
        <section id="technical" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-tools-line w-8 h-8 flex items-center justify-center"></i>
            Technical Capabilities
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-md">
            <div className="space-y-5">
              {technicalCapabilities.map((capability, index) => (
                <div key={index} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <div>
                    <strong className="text-slate-900">{capability.title}:</strong>{' '}
                    <span className="text-slate-700">{capability.skills}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Education & Honors */}
        <section id="education" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-graduation-cap-line w-8 h-8 flex items-center justify-center"></i>
            Education & Honors
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-md">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-slate-900 mb-2">USC Marshall School of Business</h3>
              <p className="text-slate-700 mb-1">B.S. in Business Administration, Entrepreneurial Studies</p>
              <p className="text-emerald-700 italic">Top Business Plan, Lloyd Greif Competition</p>
            </div>
            <div className="border-t border-slate-200 pt-6">
              <h4 className="text-lg font-semibold text-slate-800 mb-4">Honors & Recognition</h4>
              <ul className="space-y-3">
                {honors.map((honor, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-700">
                    <i className="ri-award-line text-emerald-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                    <span>{honor}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Volunteer & Advisory Activities */}
        <section id="volunteer" className="mb-16 scroll-mt-20">
          <h2 className="text-3xl font-bold text-emerald-700 mb-6 flex items-center gap-3">
            <i className="ri-heart-line w-8 h-8 flex items-center justify-center"></i>
            Volunteer & Advisory Activities
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-md space-y-6">
            {volunteerActivities.map((activity, index) => (
              <div key={index} className="flex items-start gap-3 text-slate-700">
                <i className="ri-checkbox-circle-line text-emerald-600 mt-1 w-5 h-5 flex items-center justify-center"></i>
                <div>
                  <strong className="text-slate-900">{activity.organization}</strong> — {activity.role} ({activity.period})
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="text-center py-8 border-t border-slate-200">
          <p className="text-slate-600 text-sm">
            © {new Date().getFullYear()} Ricardo Tejada • Built with care
          </p>
          <p className="text-slate-500 text-xs mt-2">
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-600 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
}
